/**
 * Created by Juno Peng on 12/6/2023.
 * Description:
 * MDL template edit/copy/delete button
 * ------ maintenance history ------
 * Created by Alex on 5/31/2023.
 * Updated by Daniel Wang on 6/5/2024 refactor the component. (add copy notification id and download sample scription function, change edit/copy/delete as meatball)
 */

import { ChangeDetectorRef, Component, ViewEncapsulation } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { SlideSheetService } from '../../../../services/slide-sheet.service';
import { SlideSheetActionTypes } from '../../../slide-sheet/slide-sheet.model';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { NotificationService } from '../../../../tamalelibs/services/notification.service';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { AdminNotificationActionService, NotificationActionType } from './admin-notification.service';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../../widgets/tam-context-menu/tam-context-menu.model';
import { AlertBtnTypes, AlertButton } from '../../../../widgets/alert-window/alert-window.model';
import { CopyService } from '../../../../services/copy.service';
import { FileHelperService } from '../../../../services/file-helper.service';
import { take } from 'rxjs/operators';
import { API_SUBSCRIPTION_ID } from '../admin-page-editors/notification-editor/notification-editor.model';

@Component({
    selector: 'tam-admin-notification-actions',
    template: `
        <div class="button-group">
            <div [style.visibility]="isAPISubscriptionChecked ? 'visible' : 'hidden'" kendoTooltip position="bottom" (click)='onCopyId()' class='button-group-icon-copy' title="Copy Id"><smart-icon #anchor name="info-o" class="light" title="Get ID"></smart-icon></div>
            <div [style.visibility]="isAPISubscriptionChecked ? 'visible' : 'hidden'" kendoTooltip tooltipClass="tooltip" position="bottom" (click)='onDownload()' class='button-group-icon-download' title="Download script"><smart-icon #anchor name="download" class="light"></smart-icon></div>
            <div (click)="showContextMenu($event)" class='button-group-icon-metaball'>
                <smart-icon name="meatball" class="light"></smart-icon>
            </div>
            <tam-context-menu [config]="contextMenuConfig"></tam-context-menu>
        </div>
    `,
    encapsulation: ViewEncapsulation.None,
    styles: [`
        .button-group {
            display: flex;
            color: $brightBlue;
            padding-top: 4px;

            .button-group-icon-copy {
                margin-right: 15px !important;
                margin: auto;
            }

            .button-group-icon-download {
                margin-right: 15px;
            }

            .button-group-icon-metaball {
                margin-left: 0px !important;
                margin: auto;
            }

            smart-icon {
                font-size: 18px;
            }
        }

        .tooltip * {
            white-space: nowrap !important;
        }
    `],
})
export class AdminNotificationActionsComponent implements ICellRendererAngularComp {
    contextMenuConfig: ContextMenuConfig;
    isAPISubscriptionChecked = false;
    params: any;

    constructor(
        private _alertWindow: AlertWindowService,
        private _adminNotificationActionService: AdminNotificationActionService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _copyService: CopyService,
        private _fileHelperService: FileHelperService,
        private _notificationService: NotificationService,
        private _toast: ToastService,
        private _slideSheetService: SlideSheetService,
    ) { }

    agInit(params: any): void {
        this.params = params;
        if (this.params?.data?.configuration?.actionType?.includes(API_SUBSCRIPTION_ID)) {
            this.isAPISubscriptionChecked = true;
        }
        this._initContextMenu();
    }

    /**
     * copy notification id
     */
    onCopyId() {
        const cancelButton = new AlertButton();
        cancelButton.text = 'Cancel';
        cancelButton.type = AlertBtnTypes.tertiary;
        cancelButton.visible = true;

        const copyButton = new AlertButton();
        copyButton.text = 'Copy ID';
        copyButton.type = AlertBtnTypes.primary;
        copyButton.visible = true;

        const notificationId = this.params.data?.id;

        const subscription = this._alertWindow.custom(
            this.params.data?.name,
            ['ID: ' + notificationId],
            cancelButton,
            copyButton,
            650,
            '',
            '',
            true
        ).subscribe((result: boolean) => {
            if (result) {
                this._copyService.copy(notificationId);
                this._toast.notify({
                    message: 'Notification ID copied to clipboard.',
                    style: NotificationStyles.Success
                });
            }
            subscription.unsubscribe();
        });
    }

    /**
     * download sample script from server
     */
    onDownload() {
        this._notificationService.downloadSampleScript(this.params.data?.id).pipe(take(1)).subscribe(data => {
            const context = data.body; // get script from response
            const name = data.headers.get('content-disposition').split('"')[1]; // get file name from response header
            this._fileHelperService.downloadFunction(context, name);
            this._toast.notify({
                message: 'Notification API subscription script downloaded successfully.',
                style: NotificationStyles.Success
            });
        }, error => {
            console.log('download sample script failed. error: ' + error);
        });
    }

    refresh(): boolean {
        return false;
    }

    showContextMenu(e: MouseEvent) {
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(e));
    }

    /**
     * init context menu
     */
    private _initContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.showItemIcon = false;
        this.contextMenuConfig.items = [{
            id: '0',
            text: 'Edit'
        }, {
            id: '1',
            text: 'Copy'
        }, {
            id: '2',
            text: 'Delete'
        }];
        this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event));
    }

    /**
     * trigger event by the clicked item
     * @param event
     */
    private _onMenuSelect(event) {
        const notificationData = this.params.data;
        const type = event.payload.id;
        switch (type) {
            case '0':
                // edit
                notificationData.isCopy = false;
                if (notificationData) {
                    this._slideSheetService.slideSheetActionSubject$.next({
                        type: SlideSheetActionTypes.ADMIN_CONSOLE_NOTIFICATION_EDITOR,
                        payload: notificationData
                    });
                }
                break;
            case '1':
                // copy
                notificationData.isCopy = true;
                if (notificationData) {
                    this._slideSheetService.slideSheetActionSubject$.next({
                        type: SlideSheetActionTypes.ADMIN_CONSOLE_NOTIFICATION_EDITOR,
                        payload: notificationData
                    });
                }
                break;
            case '2':
                // delete
                const notification = Object.assign({}, this.params.data);
                this._changeDetectorRef.detectChanges();

                const message = `Are you sure you want to delete ${notification.name}?`;
                const subscription = this._alertWindow.warn(StringLiteralsPipe.translate('general.delete_confirmation_required'),
                    [message], 'Delete', 'Cancel')
                    .subscribe((result: boolean) => {
                        if (result) {
                            this._notificationService.deleteNotifications(notification.id).subscribe(() => {
                                this._toast.notify({
                                    message: notification.name + ' is deleted successfully.',
                                    style: NotificationStyles.Success
                                });
                            });

                            this._adminNotificationActionService.subject$.next({
                                type: NotificationActionType.DELETE_NOTIFICATION,
                                payload: notification
                            });
                        }
                        subscription.unsubscribe();
                    });
                break;
            default:
                break;
        }
    }
}
